/**
 * sindhi-reveal.js
 * Sindhi Dry Fruits — scroll-into-view reveal (replaces GSAP + ScrollTrigger)
 * ─────────────────────────────────────────────────────────────────────────────
 * Replaces assets/sindhi-motion.js, which pulled 115KB of GSAP + ScrollTrigger
 * from cdn.jsdelivr.net on EVERY collection template. Measured 2026-08-23 on
 * live /collections/gift-items (Lighthouse, mobile emulation + 4x CPU throttle):
 * jsdelivr accounted for 4,365ms of main-thread bootup — roughly 6x the cost of
 * every other theme asset on the page combined — to run three decorative
 * fade-ins. CSS animations do the same job for zero network cost.
 *
 * Usage — one attribute on the section root, no per-item markup:
 *   <section data-sm-reveal="[data-spc-card]"> … </section>
 * The attribute value is a selector, resolved within the root, naming the
 * elements to stagger in.
 *
 * Per-section tuning via CSS custom properties on the root (see
 * assets/sindhi-brand-global.css for the defaults):
 *   --sm-y        travel distance     (default 12px)
 *   --sm-dur      duration            (default .4s)
 *   --sm-stagger  per-item delay step (default .05s)
 *   --sm-ease     timing function
 *
 * SAFETY (2026-05-21 incident): nothing is hidden by a stylesheet alone. The
 * hiding state lives on data-sm="pending", which only this script sets, and it
 * is set in the same synchronous block that constructs the observer. If the
 * script never runs, never parses, or bails on an unsupported browser, every
 * section renders fully visible. That is the opposite of the GSAP arrangement,
 * where a CDN failure left grid, trust strip and smart banners permanently
 * blank.
 */

(function () {
  'use strict';

  /* No observer support, or the visitor asked for less motion — leave every
     target in its natural, fully visible state. */
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Roots already armed. WeakSet so re-rendered sections (AJAX refilter,
     theme editor) are new nodes and get armed again on their own. */
  var armed = new WeakSet();

  /* GSAP's ScrollTrigger `start: 'top 85%'` fired when the trigger's top
     reached 85% down the viewport. A -15% bottom root margin is the direct
     IntersectionObserver equivalent. */
  var OBSERVER_OPTIONS = { threshold: 0, rootMargin: '0px 0px -15% 0px' };

  function arm(root) {
    if (armed.has(root)) return;

    var selector = root.getAttribute('data-sm-reveal');
    if (!selector) return;

    var items;
    try {
      items = root.querySelectorAll(selector);
    } catch (e) {
      /* Bad selector in a section — never let it hide content. */
      return;
    }
    if (!items.length) return;

    armed.add(root);

    for (var i = 0; i < items.length; i++) {
      items[i].style.setProperty('--sm-i', i);
      items[i].setAttribute('data-sm', 'pending');
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var j = 0; j < entries.length; j++) {
        if (!entries[j].isIntersecting) continue;

        /* GSAP used `once: true` — reveal is one-shot, never replayed. */
        observer.disconnect();

        for (var k = 0; k < items.length; k++) reveal(items[k]);
        return;
      }
    }, OBSERVER_OPTIONS);

    observer.observe(root);
  }

  function reveal(el) {
    /* Strip the attribute once the animation finishes so the element returns to
       its natural state. Leaving `animation: … both` in place would keep the
       filled `transform: none` winning over the card's own :hover transform —
       and a permanent transform on a card is what trapped the closed variant
       sheet inside it back when GSAP drove this (see assets/sindhi-cards.css,
       the .qvs[open] note).

       The animationName guard matters: cards carry other animations, and an
       unrelated one finishing first would strip data-sm mid-reveal and pop the
       element to full opacity. */
    el.addEventListener('animationend', function onEnd(event) {
      if (event.animationName !== 'sindhi-sm-rise') return;
      el.removeEventListener('animationend', onEnd);
      el.removeAttribute('data-sm');
      el.style.removeProperty('--sm-i');
    });

    el.setAttribute('data-sm', 'in');
  }

  function scan() {
    var roots = document.querySelectorAll('[data-sm-reveal]');
    for (var i = 0; i < roots.length; i++) arm(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  /* Theme editor re-render and Section Rendering API swaps. */
  document.addEventListener('shopify:section:load', scan);
})();
